import { ComponentRef, Injectable, OnDestroy, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";

import {
  AcademyAppArticleEntry,
  PayloadAPIService,
} from "@core/api/payload/payload.service";
import { AccessService } from "@core/services/access.service";
import { RouteMatch } from "@core/utils/router-match-helper";

import { Observable, Subject, filter, takeUntil, tap } from "rxjs";

import { CHelpGuideComponent } from "../components/help-guide/help-guide.component";
import { PageArticle } from "../components/help-guide/models";
import { ComponentOutletService } from "./confect-component-outlet.service";

export interface HelpRef {
  close: (e) => void;
  open: (e) => void;
  options: any;
}

@Injectable({ providedIn: "root" })
export class HelpService implements OnDestroy {
  private router = inject(Router);
  private outletService = inject(ComponentOutletService);
  private payloadService = inject(PayloadAPIService);
  private accessService = inject(AccessService);

  unsubsribe$$ = new Subject();

  unreadCount: number = 0;

  helpComponent: ComponentRef<CHelpGuideComponent>;

  isInitialized: boolean = false;

  articles: AcademyAppArticleEntry[] = [];

  inspirationalArticles: PageArticle[] = [
    {
      title: "Catalog Ads for Meta",
      link: "https://confect.io/blog/catalog-ads-guide-for-meta-facebook",
      subtitle: "The ultimate guide for designing catalog ads on Facebook",
    },
    {
      title: "Catalog Ads designs",
      link: "https://confect.io/tactics/catalog-ads-designs-the-big-cheat-sheet",
      subtitle: "The big cheat sheet for higher performance",
    },
    {
      title: "Advantage+ Shopping Campaigns ",
      link: "https://confect.io/tactics/advantage-shopping-campaigns-best-setup",
      subtitle: "Learn from the best ecommerce brands",
    },
  ];
  public unreadSubject: Subject<number> = new Subject<number>();

  public get unreadObservable(): Observable<any> {
    return this.unreadSubject.asObservable().pipe(
      tap((res) => {
        this.unreadCount = res;
      }),
    );
  }

  /** Destroys the `content` and the `modal` itself. */
  private static destroyContent(content: ComponentRef<any>): void {
    content.destroy();
  }

  getRouteArticles(route: string, articleEntries: AcademyAppArticleEntry[]) {
    const articles = articleEntries
      .filter((path) => RouteMatch.match(route, path.paths))
      .map((r) => r.articles)
      .flat(1)
      .map((article) => {
        return {
          title: article.name,
          link: `https://academy.confect.io/articles/read/${article.slug}`,
          subtitle: article.subtitle,
        };
      });
    return articles;
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const accessService = this.accessService;

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubsribe$$),
      )
      .subscribe({
        next: (e: NavigationEnd) => {
          if (accessService.current == null) {
            this.helpComponent != null &&
              HelpService.destroyContent(this.helpComponent);
            this.isInitialized = false;
            return;
          }
          const articles = this.getRouteArticles(
            e.url.split("?")[0],
            this.articles,
          );
          if (!this.isInitialized) {
            this.init(e.url.split("?")[0]);
          } else {
            this.load({
              items: [],
              articles:
                articles.length > 0 ? articles : this.inspirationalArticles,
              inspiration: articles.length === 0,
            });
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.unsubsribe$$.next(true);
  }

  init(path: string) {
    this.payloadService.getAppArticles().subscribe({
      next: (res) => {
        this.articles = res;
        const articles = this.getRouteArticles(path, res);

        this.initComponent({
          items: [],
          articles: articles.length > 0 ? articles : this.inspirationalArticles,
          inspiration: articles.length === 0,
        });
        this.isInitialized = true;

        this.load({
          items: [],
          articles: articles.length > 0 ? articles : this.inspirationalArticles,
          inspiration: articles.length === 0,
        });
      },
    });
  }

  initComponent(opts) {
    this.helpComponent = this.outletService.open({
      component: CHelpGuideComponent,
      inputs: {
        items: opts.items,
        articles: opts.articles,
        inspiration: opts.inspiration,
        boxTitle: opts.title,
        unreadCount: this.unreadCount,
      },
    }).componentRef as ComponentRef<CHelpGuideComponent>;
  }

  load(opts) {
    const helpRef: HelpRef = {
      options: opts,
    } as HelpRef;

    opts.event?.preventDefault();

    this.helpComponent.instance.close(opts.event);
    this.helpComponent.instance.closeLiveChat(opts.event);

    this.helpComponent.setInput("items", opts.items);
    this.helpComponent.setInput("boxTitle", opts.title);
    this.helpComponent.setInput("inspiration", opts.inspiration);
    if (opts.articles != null) {
      this.helpComponent.setInput("articles", opts.articles);
    }

    this.helpComponent.setInput("unreadCount", this.unreadCount);

    helpRef.close = (e) => this.helpComponent.instance.close(e);
    helpRef.open = (e) => this.helpComponent.instance.open(e);

    return helpRef;
  }
}
